import { API_PATH, BASE_PATH, API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Divider,
    IconButton,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NavigationIcon from '@mui/icons-material/Navigation';
import MapIcon from '@mui/icons-material/Map';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import TrainIcon from '@mui/icons-material/Train';
import ActivityImageGallery from './ActivityImageGallery';
import AIIcon from './AIIcon';
import { getActivityDisplayTitle, getActivityOriginalTitle } from '../utils/activityUtils';

// Official brand SVG icons
const GoogleMapsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 92.3 132.3" width="16" height="16">
        <path fill="#1a73e8" d="M60.2 2.2C55.8.8 51 0 46.1 0 32 0 19.3 6.4 10.8 16.5l21.8 18.3L60.2 2.2z"/>
        <path fill="#ea4335" d="M10.8 16.5C4.1 24.5 0 34.9 0 46.1c0 8.7 1.7 15.7 4.6 22l28-33.3-21.8-18.3z"/>
        <path fill="#4285f4" d="M46.2 28.5c9.8 0 17.7 7.9 17.7 17.7 0 4.3-1.6 8.3-4.2 11.4 0 0 13.9-16.6 27.5-32.7-5.6-10.8-15.3-19-27-22.7L32.6 34.8c3.3-3.8 8.1-6.3 13.6-6.3"/>
        <path fill="#fbbc04" d="M46.2 63.8c-9.8 0-17.7-7.9-17.7-17.7 0-4.3 1.5-8.3 4.1-11.3l-28 33.3c4.8 10.6 12.8 19.2 21 29.9l34.1-40.5c-3.3 3.9-8.1 6.3-13.5 6.3"/>
        <path fill="#34a853" d="M59.1 109.2c15.4-24.1 33.3-35 33.3-63 0-7.7-1.9-14.9-5.2-21.3L25.6 98c2.6 3.4 5.3 7.3 7.9 11.3 9.4 14.5 6.8 23.1 12.8 23.1s3.4-8.7 12.8-23.2"/>
    </svg>
);

const WazeIcon = () => (
    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122.71 122.88" width="16" height="16">
        <g>
            <path fill="#FFFFFF" d="M55.14,104.21c4.22,0,8.44,0.19,12.66-0.09c3.84-0.19,7.88-0.56,11.63-1.5c29.82-7.31,45.76-40.23,32.72-68.07 C104.27,17.76,90.77,8.19,72.3,6.22c-14.16-1.5-26.82,2.72-37.51,12.28c-10.5,9.47-15.94,21.28-16.31,35.44 c-0.09,3.28,0,6.66,0,9.94C18.38,71.02,14.35,76.55,7.5,78.7c-0.09,0-0.28,0.19-0.38,0.19c2.63,6.94,13.31,17.16,19.97,19.69 C35.45,87.14,52.32,91.18,55.14,104.21L55.14,104.21z"/>
            <path fill="#00D4FF" d="M54.95,110.49c-1.03,4.69-3.56,8.16-7.69,10.31c-5.25,2.72-10.6,2.63-15.57-0.56c-5.16-3.28-7.41-8.25-7.03-14.35 c0.09-1.03-0.19-1.41-1.03-1.88c-9.1-4.78-16.31-11.44-21.28-20.44c-0.94-1.78-1.69-3.66-2.16-5.63c-0.66-2.72,0.38-4.03,3.19-4.31 c3.38-0.38,6.38-1.69,7.88-4.88c0.66-1.41,1.03-3.09,1.03-4.69c0.19-4.03,0-8.06,0.19-12.1c1.03-15.57,7.5-28.5,19.32-38.63 C42.67,3.97,55.42-0.43,69.76,0.03c25.04,0.94,46.51,18.57,51.57,43.23c4.59,22.32-2.34,40.98-20.07,55.51 c-1.03,0.84-2.16,1.69-3.38,2.44c-0.66,0.47-0.84,0.84-0.56,1.59c2.34,7.13-0.94,15-7.5,18.38c-8.91,4.41-19.22-0.09-21.94-9.66 c-0.09-0.38-0.56-0.84-0.84-0.84C63.11,110.4,59.07,110.49,54.95,110.49L54.95,110.49z M55.14,104.21c4.22,0,8.44,0.19,12.66-0.09 c3.84-0.19,7.88-0.56,11.63-1.5c29.82-7.31,45.76-40.23,32.72-68.07C104.27,17.76,90.77,8.19,72.3,6.22 c-14.16-1.5-26.82,2.72-37.51,12.28c-10.5,9.47-15.94,21.28-16.31,35.44c-0.09,3.28,0,6.66,0,9.94 C18.38,71.02,14.35,76.55,7.5,78.7c-0.09,0-0.28,0.19-0.38,0.19c2.63,6.94,13.31,17.16,19.97,19.69 C35.45,87.14,52.32,91.18,55.14,104.21L55.14,104.21z"/>
            <path fill="#000" d="M74.92,79.74c-11.07-0.56-18.38-4.97-23.07-13.78c-1.13-2.16-0.09-4.31,2.06-4.78c1.31-0.28,2.53,0.66,3.47,2.16 c1.22,1.88,2.44,3.75,4.03,5.25c8.81,8.34,23.25,5.72,28.79-5.06c0.66-1.31,1.5-2.34,3.09-2.34c2.34,0.09,3.66,2.44,2.63,4.59 c-2.91,5.91-7.5,10.22-13.69,12.28C79.51,78.99,76.7,79.36,74.92,79.74L74.92,79.74z"/>
            <path fill="#000" d="M55.32,48.98c-3.38,0-6.09-2.72-6.09-6.09s2.72-6.09,6.09-6.09s6.09,2.72,6.09,6.09C61.42,46.17,58.7,48.98,55.32,48.98 L55.32,48.98z"/>
            <path fill="#000" d="M98.27,42.79c0,3.38-2.72,6.09-6,6.19c-3.38,0-6.09-2.63-6.09-6.09c0-3.38,2.63-6.09,6-6.19 C95.46,36.7,98.17,39.42,98.27,42.79L98.27,42.79z"/>
        </g>
    </svg>
);

const AppleMapsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="16" height="16">
        <path fill="#e0e0e0" d="M35.13 42H19V30C19 30 35.48 42 35.13 42zM6.3 10.87c.1-.33.22-.65.38-.96C7.36 8.46 8.54 7.3 10 6.64 10.149 6.565 11 13 11 13S6.193 11.207 6.3 10.87z"/>
        <path fill="#7cb342" d="M18 6H20V23H18z"/>
        <path fill="#ffcdd2" d="M11,18v23.73c-0.35-0.09-0.68-0.21-1-0.37c-2.36-1.08-4-3.47-4-6.23V18H11z"/>
        <path fill="#aed581" d="M42,25v10.13c0,0.36-0.03,0.71-0.09,1.05L20,20.6V6h3C23,16.48,31.52,25,42,25z"/>
        <path fill="#bdbdbd" d="M12 14h-2V6.64c.32-.16.65-.28 1-.37.32-.1.66-.17 1-.2C12.28 6.02 12 14 12 14zM18 32H20V42H18z"/>
        <path fill="#f9a825" d="M42,35.02v0.11c0,0.36-0.03,0.71-0.09,1.05c-0.06,0.43-0.16,0.84-0.31,1.23 c-0.82,2.36-2.9,4.13-5.44,4.5C35.83,41.97,35.48,42,35.13,42h-0.57l-3.02-2.15c-0.01,0-0.01-0.01-0.01-0.01l-4.5-3.2 c0,0-0.01,0-0.01-0.01l-6.36-4.52L20,31.65l-1-0.72l-1-0.71l-0.91-0.64L16.28,29l-2.49-1.77L12.06,26L12,25.96l-1-0.71l-1-0.72 L9.26,24H9.25L6,21.69v-8.82c0-0.69,0.1-1.37,0.3-2c0.1-0.33,0.22-0.65,0.38-0.96L10,12.27l2,1.42l1,0.71l5,3.55l1,0.71l0.55,0.39 L20,19.37v0.01l14.73,10.47l1.6,1.14l2.66,1.89l0.98,0.7L42,35.02z"/>
        <path fill="#fdd835" d="M41.91,36.18c-0.06,0.43-0.16,0.84-0.31,1.23c-0.82,2.36-2.9,4.13-5.44,4.5l-2.72-1.93 c-0.01,0.01-0.01,0.01-0.02,0l-1.49-1.08l-4.15-2.94c0,0,0,0-0.01-0.01l-1.51-1.08l-5.07-3.6L20,30.42l-1-0.71L18,29l-1.53-1.09 h-0.01l-2.31-1.65L13,25.45l-1-0.72l-1-0.71l-1-0.71l-0.11-0.08L6,20.47v-7.6c0-0.69,0.1-1.37,0.3-2L10,13.5l1,0.71l2,1.42l5,3.55 l1,0.71l1,0.71v0.01l12.41,8.81l1.89,1.35c0,0,0,0.01,0.01,0.01l4.6,3.26l0.88,0.63L41.91,36.18z"/>
        <path fill="#ef9a9a" d="M12,33v8.94c-0.34-0.04-0.68-0.11-1-0.21c-0.35-0.09-0.68-0.21-1-0.37V32L12,33z"/>
        <path fill="#fafafa" d="M19,6v36h-6.13c-0.3,0-0.59-0.02-0.87-0.06c-0.34-0.04-0.68-0.11-1-0.21V6.27 c0.32-0.1,0.66-0.17,1-0.2C12.28,6.02,12.57,6,12.87,6H19z"/>
        <path fill="#3996e8" d="M18,6v17h-6V6.052C12.28,6.015,12.57,6,12.87,6H18z"/>
        <path fill="#1976d2" d="M38.77,29.04c0,0-0.77,0.96-2.77,0.96s-3-1-3-1s-1,1-3,1s-2.77-0.96-2.77-0.96 C26.45,30.16,26,31.53,26,33c0,3.87,3.13,7,7,7s7-3.13,7-7C40,31.53,39.55,30.16,38.77,29.04z"/>
        <path fill="#d84315" d="M38,33H28c0-0.42,0.05-0.83,0.15-1.23C28.72,31.92,29.34,32,30,32c1.28,0,2.29-0.31,3-0.64 c0.71,0.33,1.72,0.64,3,0.64c0.66,0,1.28-0.08,1.85-0.23C37.95,32.17,38,32.58,38,33z"/>
        <path fill="#fbc02d" d="M10 12.27L10 13.5 10 24.53 11 25.25 11 14.21 11 12.98zM20 19.38L20 31.65 19 30.93 19 18.66z"/>
        <path fill="#1976d2" d="M15 21A7 7 0 1 0 15 35A7 7 0 1 0 15 21Z"/>
        <path fill="#e89c23" d="M10 12.27L10 13.5 11 14.21 11 12.98zM19 18.66L19 19.89 20 20.6 20 19.37z"/>
        <path fill="#e9e9e9" d="M42,16.96V26c-11.03,0-20-8.97-20-20h9.04C31.52,11.83,36.17,16.48,42,16.96z"/>
        <path fill="#7cb342" d="M42,13v4.96C35.62,17.48,30.52,12.38,30.05,6H35C38.87,6,42,9.13,42,13z"/>
        <path fill="#aed581" d="M42,13v3.96C36.17,16.48,31.52,11.83,31.04,6H35C38.87,6,42,9.13,42,13z"/>
        <path fill="#7cb342" d="M23,6h-1c0,11.03,8.97,20,20,20v-1C31.52,25,23,16.48,23,6z"/>
        <path fill="#fafafa" d="M15 24L12 32 15 30 18 32z"/>
        <path fill="#fff" d="M38.45,30.48C37.88,30.76,37.08,31,36,31c-1.39,0-2.39-0.41-3-0.77C32.39,30.59,31.39,31,30,31 c-1.08,0-1.88-0.24-2.45-0.52C27.19,31.26,27,32.11,27,33c0,3.31,2.69,6,6,6s6-2.69,6-6C39,32.11,38.81,31.26,38.45,30.48z M33,38 c-2.415,0-4.434-1.721-4.899-4h9.798C37.434,36.279,35.415,38,33,38z M28,33c0-0.422,0.051-0.834,0.151-1.233 C28.724,31.922,29.343,32,30,32c1.283,0,2.288-0.308,3-0.641C33.712,31.692,34.717,32,36,32c0.657,0,1.276-0.078,1.849-0.233 C37.949,32.166,38,32.578,38,33H28z"/>
        <path fill="#fff" d="M32.125 35.24c0 0 0-.625-.625-.625s-.625.625-.625.625h.375c0-.114.043-.25.25-.25.066 0 .242 0 .25.25 0 .215-.291.549-.487.707l-.013.01-.375.325v.036.298h1.25V36.24h-.628C31.497 36.24 32.125 35.74 32.125 35.24zM33.4 35.615c.125-.102.225-.243.225-.427 0-.316-.28-.573-.625-.573s-.625.257-.625.573c0 .184.1.325.225.427-.125.102-.225.243-.225.427 0 .316.28.573.625.573s.625-.257.625-.573C33.625 35.858 33.525 35.717 33.4 35.615zM33 34.99c.135 0 .25.091.25.198 0 .106-.157.19-.25.228-.092-.037-.25-.121-.25-.228C32.75 35.079 32.862 34.99 33 34.99zM33 36.24c-.138 0-.25-.089-.25-.198 0-.106.157-.19.25-.228.092.037.25.121.25.228C33.25 36.149 33.135 36.24 33 36.24zM34.499 34.99c.104 0 .138.033.163.067.057.075.087.207.087.382v.354c0 .175-.03.307-.086.381-.026.034-.06.067-.162.067-.105 0-.139-.033-.165-.067-.057-.075-.087-.206-.087-.38v-.355c0-.175.03-.307.086-.381C34.362 35.023 34.396 34.99 34.499 34.99M34.499 34.615c-.199 0-.352.071-.461.214s-.163.346-.163.608v.355c0 .261.055.463.164.607.109.144.264.215.463.215.198 0 .351-.071.46-.214.109-.143.163-.346.163-.608v-.354c0-.262-.055-.465-.164-.608C34.852 34.687 34.698 34.615 34.499 34.615L34.499 34.615z"/>
    </svg>
);

// FlightAware identifies flights by ICAO airline code (3 letters) rather than
// the IATA code (2 chars) shown on tickets, e.g. OS84 must become AUA84 for
// Austrian Airlines. Mirror of the mapping in backend/api/flightaware_scraper.php.
const IATA_TO_ICAO = {
    ET: 'ETH', LY: 'ELY', AA: 'AAL', UA: 'UAL', DL: 'DAL', BA: 'BAW',
    AF: 'AFR', LH: 'DLH', EK: 'UAE', QR: 'QTR', TK: 'THY', SQ: 'SIA',
    CX: 'CPA', NH: 'ANA', JL: 'JAL', KL: 'KLM', IB: 'IBE', AZ: 'ITY',
    OS: 'AUA', LX: 'SWR', SK: 'SAS', AY: 'FIN', EI: 'EIN', VS: 'VIR',
    WN: 'SWA', B6: 'JBU', AS: 'ASA', F9: 'FFT', NK: 'NKS', AC: 'ACA',
    QF: 'QFA', NZ: 'ANZ', CA: 'CCA', MU: 'CES', CZ: 'CSN', KE: 'KAL',
    OZ: 'AAR', AI: 'AIC', '6E': 'IGO', SG: 'SEJ', G8: 'GOW', UK: 'VTI',
};

// Build the FlightAware flight identifier from a raw flight number like
// "OS 84" / "OS84". Splits the 2-char IATA carrier prefix from the number and
// swaps in the ICAO code when known; otherwise returns the number unchanged.
const buildFlightAwareId = (rawFlightNum) => {
    const cleaned = (rawFlightNum || '').replace(/\s+/g, '').toUpperCase();
    const m = cleaned.match(/^([0-9A-Z]{2})(\d+)$/);
    if (m && IATA_TO_ICAO[m[1]]) {
        return IATA_TO_ICAO[m[1]] + m[2];
    }
    return cleaned;
};

const ActivityDetailsModal = ({ open, onClose, activity, trip, onEdit, onDelete, onPin, onActivityUpdated }) => {
    const { t, i18n } = useTranslation();
    const [sourceEmailMsg, setSourceEmailMsg] = useState(null);
    const [loadingSourceEmail, setLoadingSourceEmail] = useState(false);

    const handleViewSourceEmail = async () => {
        if (!activity?.source_message_id) return;
        setLoadingSourceEmail(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/get_message.php?id=${activity.source_message_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setSourceEmailMsg(await res.json());
        } catch (err) {
            console.error('Failed to load source email', err);
        } finally {
            setLoadingSourceEmail(false);
        }
    };

    const [viatorDetails, setViatorDetails] = useState(null);
    const [loadingViator, setLoadingViator] = useState(false);
    const [localProductCode, setLocalProductCode] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [loadingComparison, setLoadingComparison] = useState(false);
    const [flightPrice, setFlightPrice] = useState(null);
    const [loadingFlightPrice, setLoadingFlightPrice] = useState(false);
    const [flightStatus, setFlightStatus] = useState(null);
    const [loadingFlightStatus, setLoadingFlightStatus] = useState(false);
    // Schedule history / rollback UI state.
    const [showScheduleHistory, setShowScheduleHistory] = useState(false);
    const [rollbackBusy, setRollbackBusy] = useState(-1);
    const [rollbackError, setRollbackError] = useState('');
    // Ticks every 30s while the modal is open so "Updated X min ago" stays fresh.
    const [statusAgoTick, setStatusAgoTick] = useState(0);
    useEffect(() => {
        if (!open || !flightStatus?.fetched_at) return;
        const id = setInterval(() => setStatusAgoTick(t => t + 1), 30000);
        return () => clearInterval(id);
    }, [open, flightStatus?.fetched_at]);
    
    const getActivityDate = () => {
        if (!activity || !trip) return null;
        
        // If activity has a specific date, use it
        if (activity.day_date) return activity.day_date;
        
        // If trip has start date and activity has day number, calculate date
        if (trip.start_date && activity.day) {
            const startDate = new Date(trip.start_date);
            const activityDate = new Date(startDate);
            activityDate.setDate(startDate.getDate() + (parseInt(activity.day) - 1));
            return activityDate.toISOString().split('T')[0];
        }
        
        return null;
    };

    // Live flight status should only surface close to departure: gate/baggage/
    // delay info is meaningless days out, and FlightAware can return a past
    // recurrence of the same daily flight. Window: from 48h before scheduled
    // departure onward (and through/after departure, when status is most useful).
    const FLIGHT_STATUS_WINDOW_HOURS = 48;
    const getFlightDepartureDateTime = () => {
        if (!activity) return null;
        const sd = (activity.structured_data && typeof activity.structured_data === 'object')
            ? activity.structured_data : {};
        // Importer stores "YYYY-MM-DD HH:MM"
        if (typeof sd.start_date_time === 'string' && sd.start_date_time.trim()) {
            const d = new Date(sd.start_date_time.replace(' ', 'T'));
            if (!isNaN(d.getTime())) return d;
        }
        const date = getActivityDate();
        if (date) {
            const time = activity.start_time || '00:00:00';
            const d = new Date(`${date}T${time}`);
            if (!isNaN(d.getTime())) return d;
        }
        return null;
    };
    const isWithinFlightStatusWindow = () => {
        const dep = getFlightDepartureDateTime();
        if (!dep) return false;
        const msUntilDeparture = dep.getTime() - Date.now();
        return msUntilDeparture <= FLIGHT_STATUS_WINDOW_HOURS * 60 * 60 * 1000;
    };

    const formatDateForURLParam = (dateStr) => {
        if (!dateStr) return '';
        return dateStr; // Already YYYY-MM-DD
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    };

    const getExpediaLinkWithDates = () => {
        if (!activity || !activity.expedia_affiliate_link) return null;
        
        const date = getActivityDate();
        if (!date) return activity.expedia_affiliate_link;
        
        // Try to inject date into Expedia link if possible, or just return the link
        // For now, we'll just return the link as constructing deep links is complex
        return activity.expedia_affiliate_link;
    };

    // Auto-fetch Viator details and comparison when modal opens
    useEffect(() => {
        
        if (!open) {
            setViatorDetails(null);
            setLoadingViator(false);
            setLocalProductCode(null);
            setComparison(null);
            setLoadingComparison(false);
            setFlightStatus(null);
            setLoadingFlightStatus(false);
            return;
        }

        const fetchViatorFullDetails = (productCode) => {
            setLoadingViator(true);
            fetch(`${API_PATH}/viator_full_details.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    product_code: productCode,
                    activity_id: activity.activity_id,
                    language: i18n.language
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) setViatorDetails(data);
                setLoadingViator(false);
            })
            .catch(error => {
                console.error('Error fetching Viator details:', error);
                setLoadingViator(false);
            });
        };
        
        // If modal is open and activity has Viator product code, fetch details automatically
        if (open && activity && activity.viator_product_code) {
            fetchViatorFullDetails(activity.viator_product_code);
        } else if (open && activity && !activity.viator_product_code &&
                   activity.type !== 'transportation' &&
                   activity.type !== 'lodging' &&
                   activity.type !== 'flight' &&
                   activity.title &&
                   (activity.location || activity.destination)) {
            // No product code yet — search Viator on-the-fly for any non-transport/lodging activity
            const token = localStorage.getItem('token');
            setLoadingViator(true);
            fetch(`${API_PATH}/viator_quick_price.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    activity_id: activity.activity_id,
                    location: activity.location || activity.destination,
                    destination: activity.destination || activity.location,
                    title: activity.title,
                    viator_activity_type: activity.viator_activity_type,
                    viator_activity_name: activity.viator_activity_name,
                    language: i18n.language
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.success && data.product_code) {
                    setLocalProductCode(data.product_code);
                    fetchViatorFullDetails(data.product_code);
                } else {
                    setLoadingViator(false);
                }
            })
            .catch(error => {
                console.error('Error searching Viator:', error);
                setLoadingViator(false);
            });
        }
        
        // REMOVED: compare_booking_realtime.php call - using viatorDetails.ai_comparison instead
        // The ai_comparison from viator_full_details.php already contains accurate match quality
        if (false && open && activity && (activity.viator_product_code || activity.gyg_tour_id) && !activity.title?.startsWith('Travel from')) {
            setLoadingComparison(true);
            
            const startTime = Date.now();
            const token = localStorage.getItem('token');
            // Use REAL-TIME comparison endpoint (fetches fresh prices from APIs)
            fetch(`${API_PATH}/compare_booking_realtime.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    activity_id: activity.activity_id,
                    language: i18n.language
                })
            })
            .then(response => response.json())
            .then(data => {
                
                // Extract and display Gemini analysis from debug logs
                if (data.debug) {
                    data.debug.forEach(log => {
                        
                        // Extract Gemini match quality if present
                        if (log.includes('Gemini Analysis Result:')) {
                            const match = log.match(/match=(\w+)/);
                            if (match) {
                            }
                        }
                        
                        // Extract Gemini reason if present
                        if (log.includes('Gemini Reason:')) {
                            const reason = log.replace('🤖 Gemini Reason: ', '');
                        }
                        
                        // Show decision
                        if (log.includes('DECISION:')) {
                        }
                    });
                }
                
                // Also show the final reason returned by API
                if (data.reason) {
                }
                
                if (data.success) {
                    setComparison(data);
                } else {
                }
                
                // Keep loading until comparison is set AND Gemini analysis box is rendered
                // Longer delay to ensure user sees analysis before loader disappears
                setTimeout(() => {
                    setLoadingComparison(false);
                }, 1200); // Wait for Gemini analysis to be visible on screen
            })
            .catch(error => {
                console.error('Error fetching comparison:', error);
                setTimeout(() => {
                    setLoadingComparison(false);
                }, Math.max(0, 1000 - (Date.now() - startTime)));
            });
        }
        
        // Fetch flight price for "Travel from" activities with flight subtype
        if (open && activity && activity.title?.startsWith('Travel from') && activity.subtype === 'flight') {
            const activityDate = getActivityDate();
            if (activityDate) {
                // Extract origin and destination from title
                const match = activity.title.match(/Travel from (.+?) to (.+?)$/i);
                if (match) {
                    const origin = match[1].trim();
                    const destination = match[2].trim();
                    const dateStr = formatDateForURLParam(activityDate); // YYYY-MM-DD for API
                    
                    setLoadingFlightPrice(true);
                    
                    const token = localStorage.getItem('token');
                    fetch(`${API_PATH}/fetch_flight_price.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            activity_id: activity.activity_id,
                            origin: origin,
                            destination: destination,
                            date: dateStr
                        })
                    })
                    .then(response => response.json())
                    .then(data => {
                        if (data.success) {
                            setFlightPrice(data.price);
                        }
                        setLoadingFlightPrice(false);
                    })
                    .catch(error => {
                        console.error('Error fetching flight price:', error);
                        setLoadingFlightPrice(false);
                    });
                }
            }
        }

        // ── Live flight status for booked transportation flights ──────────────
        const sd = activity.structured_data || {};
        const rawFlightNum = sd.flight_number || '';
        const isFlightTransport =
            (activity.type === 'transportation' || activity.type === 'flight') &&
            (activity.subtype === 'flight' || !activity.subtype) &&
            rawFlightNum;

        if (open && isFlightTransport && isWithinFlightStatusWindow()) {
            const activityDate = getActivityDate();
            if (activityDate) {
                setLoadingFlightStatus(true);
                const token = localStorage.getItem('token');
                fetch(`${API_PATH}/get_flight_status.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        flight_number: rawFlightNum,
                        date: activityDate,
                        origin: sd.origin_airport_code || '',
                        destination: sd.destination_airport_code || '',
                    })
                })
                .then(r => r.json())
                .then(data => {
                    if (data.success) setFlightStatus(data);
                })
                .catch(err => console.error('Flight status error:', err))
                .finally(() => setLoadingFlightStatus(false));
            }
        }
    }, [open, activity?.viator_product_code, activity?.gyg_tour_id, activity?.activity_id, activity?.type]);
    
    if (!activity) return null;

    const handleClose = (event, reason) => {
        if (onClose) {
            onClose();
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'Not set';
        try {
            const time = new Date(`2000-01-01T${timeString}`);
            return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return timeString;
        }
    };

    const calculateEndTime = (startTime, duration) => {
        if (!startTime || !duration) return 'Not set';
        try {
            const start = new Date(`2000-01-01T${startTime}`);
            start.setMinutes(start.getMinutes() + parseInt(duration));
            return start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return 'Not set';
        }
    };

    const prettifyLabel = (raw) => {
        if (!raw) return '';
        const label = raw.replace(/([A-Z])/g, ' $1').replace(/[_\.]/g, ' ');
        return label.replace(/\s+/g, ' ').trim().replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const gatherFlightInfoSource = () => {
        if (!activity) return null;
        // Email-imported flights store flight fields flat on structured_data
        // (flight_number, carrier_name, origin_airport_code, ...) rather than
        // nested under flight_details. Build a curated view from those flat
        // keys first so the flight number / airline / route actually show.
        const sd = activity.structured_data || {};
        const flatLabelMap = {
            carrier_name: 'Airline',
            flight_number: 'Flight Number',
            origin_airport_code: 'From',
            destination_airport_code: 'To',
            departure_terminal: 'Departure Terminal',
            arrival_terminal: 'Arrival Terminal',
            seat_number: 'Seat Number',
        };
        const flat = {};
        Object.keys(flatLabelMap).forEach((key) => {
            const val = sd[key];
            if (val !== undefined && val !== null && val !== '') {
                flat[flatLabelMap[key]] = val;
            }
        });
        if (Object.keys(flat).length > 0) return flat;
        const candidates = [
            activity.structured_data?.flight_details,
            activity.structured_data?.details,
            activity.structured_data?.transportation_details,
            activity.flight_details,
            activity.transportation_details,
            activity.flight_info,
        ];
        return candidates.find(candidate => candidate && Object.keys(candidate).length > 0) || null;
    };

    const flightInfoSource = gatherFlightInfoSource();
    const flightInfoRows = [];

    const flattenFlightInfo = (value, path = '') => {
        if (value === undefined || value === null || value === '') {
            return;
        }
        if (Array.isArray(value)) {
            const displayValue = value.filter(Boolean).join(', ');
            if (displayValue) {
                flightInfoRows.push({ label: prettifyLabel(path), value: displayValue });
            }
            return;
        }
        if (typeof value === 'object') {
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                const nextPath = path ? `${path}.${nestedKey}` : nestedKey;
                flattenFlightInfo(nestedValue, nextPath);
            });
            return;
        }
        flightInfoRows.push({ label: prettifyLabel(path), value: value });
    };

    if (flightInfoSource) {
        flattenFlightInfo(flightInfoSource);
    }

    const hasFlightInfo = flightInfoRows.length > 0;

    // ── Schedule history / rollback ───────────────────────────────────────────
    const parseStructured = (v) => {
        if (!v) return {};
        if (typeof v === 'string') { try { return JSON.parse(v); } catch { return {}; } }
        return v;
    };
    const scheduleSd = parseStructured(activity?.structured_data);
    const scheduleHistory = Array.isArray(scheduleSd.schedule_history) ? scheduleSd.schedule_history : [];

    const handleRollback = async (index) => {
        if (!activity?.activity_id) return;
        setRollbackBusy(index);
        setRollbackError('');
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_PATH}/rollback_schedule.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ activity_id: activity.activity_id, history_index: index }),
            });
            const data = await res.json();
            if (data && data.success) {
                if (onActivityUpdated) await onActivityUpdated();
                setShowScheduleHistory(false);
            } else {
                setRollbackError((data && data.message) || 'Rollback failed.');
            }
        } catch (e) {
            setRollbackError('Rollback failed.');
        } finally {
            setRollbackBusy(-1);
        }
    };

    const getQuickPriceSymbol = (priceValue) => {
        const priceNumber = Number(priceValue);
        if (!priceValue || Number.isNaN(priceNumber) || priceNumber <= 0) {
            return '--';
        }
        if (priceNumber < 25) {
            return '$';
        }
        if (priceNumber < 75) {
            return '$$';
        }
        if (priceNumber < 200) {
            return '$$$';
        }
        return '--';
    };

    const formatViatorQuickPrice = (priceValue) => {
        const priceNumber = Number(priceValue);
        if (!priceValue || Number.isNaN(priceNumber) || priceNumber <= 0) {
            return '--';
        }
        const formatted = Number.isInteger(priceNumber)
            ? priceNumber.toLocaleString()
            : priceNumber.toFixed(2);
        return `$${formatted}`;
    };

    const locationSuffix = activity.destination || activity.location || '';
    const aiSearchTerm = viatorDetails?.ai_comparison?.search_term?.trim();
    const buildSearchQuery = (term) => {
        const baseTerm = (term || activity.title || '').trim();
        const parts = [];
        if (baseTerm) parts.push(baseTerm);
        if (locationSuffix) parts.push(locationSuffix);
        return encodeURIComponent(parts.join(' ').trim());
    };
    const bookingSearchQuery = buildSearchQuery(aiSearchTerm);
    
    // For "poor" match, always use activity title (Gemini-recommended name) as search term
    const activityTitleSearchQuery = buildSearchQuery(getActivityDisplayTitle(activity) || activity.title);
    
    const isBooked = activity.reference_ticket_no || activity.booking_agency;

    // Traveller's own cost for this item (captured from import / email parsing).
    // Lodging prefers a per-night rate; otherwise nightly is derived from total / nights.
    const costSummary = (() => {
        let s = activity.structured_data;
        if (Array.isArray(s)) s = s[0];
        if (typeof s === 'string') { try { s = JSON.parse(s); } catch (e) { s = {}; } }
        s = s || {};
        const SYM = { USD: '$', EUR: '€', GBP: '£', ILS: '₪', JPY: '¥', CNY: '¥', KRW: '₩', INR: '₹', THB: '฿', CHF: 'CHF', CAD: 'C$', AUD: 'A$', NZD: 'NZ$' };
        const toNum = (v) => { if (v === null || v === undefined) return null; const n = parseFloat(String(v).replace(/[^0-9.]/g, '')); return Number.isFinite(n) && n > 0 ? n : null; };
        const money = (amt, cur) => {
            if (amt === null) return '';
            const num = Number(amt);
            const body = Number.isInteger(num) ? num.toLocaleString() : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const c = (cur || '').trim();
            if (!c) return body;   // unknown currency → no symbol (don't imply USD)
            const up = c.toUpperCase();
            if (SYM[up]) return `${SYM[up]}${body}`;
            if (/^[^A-Za-z0-9]+$/.test(c)) return `${c}${body}`;
            return `${body} ${c}`;
        };
        const currency = s.currency || s.price_currency || '';
        const total = toNum(s.price ?? s.cost ?? s.total_price ?? s.amount);
        const perNight = toNum(s.price_per_night ?? s.cost_per_night ?? s.nightly_price);
        const nights = parseInt(s.nights, 10) || null;
        if (activity.type === 'lodging' || activity.type === 'Lodging') {
            if (perNight) {
                const totalCalc = total || (nights ? perNight * nights : null);
                let str = `${money(perNight, currency)}/night`;
                if (totalCalc) { str += ` · ${money(totalCalc, currency)} total`; if (nights) str += ` (${nights} night${nights > 1 ? 's' : ''})`; }
                return str;
            }
            if (total) {
                if (nights && nights > 1) return `${money(total / nights, currency)}/night · ${money(total, currency)} total (${nights} nights)`;
                return `${money(total, currency)} total`;
            }
            return null;
        }
        if (total) return money(total, currency);
        if (perNight) return money(perNight, currency);
        return null;
    })();

    // Determine if this activity is a booked flight with a known flight number
    const sd = activity.structured_data || {};
    const rawFlightNum = sd.flight_number || '';
    const isLiveFlight =
        (activity.type === 'transportation' || activity.type === 'flight') &&
        (activity.subtype === 'flight' || !activity.subtype) &&
        !!rawFlightNum;

    // Fetch live flight status. force=true bypasses the backend's 30-min cache.
    const runFlightStatusFetch = (force = false) => {
        const actDate = getActivityDate();
        if (!actDate || !rawFlightNum) return;
        setLoadingFlightStatus(true);
        if (force) setFlightStatus(null);
        const token = localStorage.getItem('token');
        fetch(`${API_PATH}/get_flight_status.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                flight_number: rawFlightNum,
                date: actDate,
                origin: sd.origin_airport_code || '',
                destination: sd.destination_airport_code || '',
                force: !!force,
            })
        })
            .then(r => r.json())
            .then(data => { if (data.success) setFlightStatus(data); })
            .catch(err => console.error('Flight status error:', err))
            .finally(() => setLoadingFlightStatus(false));
    };

    // "Updated X min ago" formatter (ticker hook is declared up top with the
    // other hooks so it runs before any early return).
    const formatFetchedAgo = (epochSeconds) => {
        if (!epochSeconds) return null;
        void statusAgoTick; // dependency so this recomputes on each tick
        const mins = Math.max(0, Math.floor(Date.now() / 1000 - epochSeconds) / 60);
        const m = Math.floor(mins);
        if (m < 1) return 'just now';
        if (m === 1) return '1 min ago';
        if (m < 60) return `${m} min ago`;
        const h = Math.floor(m / 60);
        return h === 1 ? '1 hour ago' : `${h} hours ago`;
    };

    // A flight can be stored either as type='flight' (in-app) or as
    // type='transportation' + subtype='flight' (email import). Treat both as
    // flights so the Flight Information block renders in either case.
    const isFlightActivity =
        activity.type === 'flight' ||
        (activity.type === 'transportation' && activity.subtype === 'flight');

    // Status colour helpers
    const statusColor = (st) => {
        const s = (st || '').toLowerCase();
        if (s.includes('on time') || s.includes('scheduled')) return { text: 'tw-text-emerald-400', bg: 'tw-bg-emerald-500/15', border: 'tw-border-emerald-500/30' };
        if (s.includes('landed') || s.includes('arrived'))    return { text: 'tw-text-blue-400',    bg: 'tw-bg-blue-500/15',    border: 'tw-border-blue-500/30'    };
        if (s.includes('en route') || s.includes('airborne')) return { text: 'tw-text-sky-400',     bg: 'tw-bg-sky-500/15',     border: 'tw-border-sky-500/30'     };
        if (s.includes('delay'))                              return { text: 'tw-text-amber-400',   bg: 'tw-bg-amber-500/15',   border: 'tw-border-amber-500/30'   };
        if (s.includes('cancel'))                             return { text: 'tw-text-red-400',     bg: 'tw-bg-red-500/15',     border: 'tw-border-red-500/30'     };
        return { text: 'tw-text-muted-fore', bg: 'tw-bg-surface-3/50', border: 'tw-border-surface-border/40' };
    };

    return (
        <>
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    background: 'var(--color-surface-2, #1a1f2e)',
                    border: '1px solid var(--color-surface-border, rgba(255,255,255,0.08))',
                    borderRadius: '1rem',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)',
                }
            }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-5 tw-pb-3 tw-border-b tw-border-surface-border/40">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <span className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-xl tw-bg-accent/20 tw-flex tw-items-center tw-justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-text-accent"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
                        </span>
                        <Typography variant="h6" component="div" className="tw-text-base tw-font-bold tw-text-muted-fore" style={{ margin: 0 }}>
                            Activity Details
                        </Typography>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-1">
                        {activity?.source_message_id && (
                            <button
                                onClick={handleViewSourceEmail}
                                aria-label="View source email"
                                title="View source email"
                                disabled={loadingSourceEmail}
                                className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-violet-400 hover:tw-bg-violet-500/10 tw-transition-all"
                            >
                                {loadingSourceEmail ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                )}
                            </button>
                        )}
                        {onEdit && (
                            <button
                                onClick={() => { handleClose(); onEdit(activity); }}
                                aria-label="Edit activity"
                                title="Edit"
                                className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-sky-400 hover:tw-bg-sky-500/10 tw-transition-all"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                        )}
                        {onPin && (
                            <button
                                onClick={() => { onPin(activity); }}
                                aria-label={activity.is_pinned ? 'Unpin activity' : 'Pin activity'}
                                title={activity.is_pinned ? 'Unpin' : 'Pin'}
                                className={`tw-p-1.5 tw-rounded-xl tw-transition-all ${
                                    activity.is_pinned
                                        ? 'tw-text-amber-400 hover:tw-bg-amber-500/10'
                                        : 'tw-text-muted hover:tw-text-amber-400 hover:tw-bg-amber-500/10'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>
                            </button>
                        )}
                        {onDelete && (
                            <button
                                onClick={() => { handleClose(); onDelete(activity); }}
                                aria-label="Delete activity"
                                title="Delete"
                                className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-red-400 hover:tw-bg-red-500/10 tw-transition-all"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                        )}
                        <button onClick={handleClose} aria-label="Close" className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        </button>
                    </div>
                </div>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 2, px: 2.5, pb: 1 }}>
                {/* Activity Title + Type */}
                <div className="tw-mb-4">
                    <h2 className="tw-text-lg tw-font-bold tw-text-muted-fore tw-mb-1.5 tw-leading-snug">
                        {getActivityDisplayTitle(activity)}
                    </h2>
                    {activity.type && activity.type !== '0' && activity.type !== 0 && (
                        <span className={`tw-inline-flex tw-items-center tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-lg ${
                            activity.type === 'flight'
                                ? 'tw-bg-sky-500/15 tw-text-sky-400'
                                : 'tw-bg-accent/15 tw-text-accent'
                        }`}>
                            {activity.type === 'flight' ? '✈️ Flight' : activity.type}
                        </span>
                    )}
                </div>

                {/* Time + Location info row */}
                <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-surface-3/50 tw-border tw-border-surface-border/40">
                    {/* Time */}
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-text-accent tw-flex-shrink-0"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        {activity.type === 'lodging' ? (() => {
                            let sd = activity.structured_data;
                            if (typeof sd === 'string') { try { sd = JSON.parse(sd); } catch { sd = {}; } }
                            sd = sd || {};
                            const ciTime = sd.check_in_date_time  ? sd.check_in_date_time.split(' ')[1]?.slice(0,5)  : activity.start_time?.slice(0,5);
                            const coTime = sd.check_out_date_time ? sd.check_out_date_time.split(' ')[1]?.slice(0,5) : null;
                            const ciDate = sd.check_in_date_time  ? sd.check_in_date_time.split(' ')[0]  : null;
                            const coDate = sd.check_out_date_time ? sd.check_out_date_time.split(' ')[0] : null;
                            return (
                                <span className="tw-text-sm tw-text-muted-fore tw-tabular-nums">
                                    {ciDate && <span className="tw-text-xs tw-text-muted tw-mr-1">{ciDate}</span>}
                                    <span className="tw-font-semibold">Check-in {ciTime || '--:--'}</span>
                                    {coTime && <>
                                        <span className="tw-mx-1.5 tw-text-muted">→</span>
                                        {coDate && <span className="tw-text-xs tw-text-muted tw-mr-1">{coDate}</span>}
                                        <span className="tw-font-semibold">Check-out {coTime}</span>
                                    </>}
                                </span>
                            );
                        })() : (
                            <>
                                <span className="tw-text-sm tw-text-muted-fore tw-tabular-nums">
                                    {formatTime(activity.start_time)} – {calculateEndTime(activity.start_time, activity.duration)}
                                </span>
                                <span className="tw-text-xs tw-text-muted">({activity.duration || 0} min)</span>
                            </>
                        )}
                    </div>

                    {/* Location */}
                    {activity.location && String(activity.location) !== '0' && (
                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-text-accent tw-flex-shrink-0"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
                            <span className="tw-text-sm tw-text-muted-fore tw-flex-1 tw-min-w-0 tw-truncate">{activity.location}</span>
                            <div className="tw-flex tw-items-center tw-gap-1">
                                <button
                                    onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(activity.location)}`, '_blank')}
                                    title="Open in Google Maps"
                                    className="tw-p-1 tw-rounded-lg hover:tw-bg-surface-3 tw-transition-all"
                                ><GoogleMapsIcon /></button>
                                <button
                                    onClick={() => window.open(`https://waze.com/ul?q=${encodeURIComponent(activity.location)}`, '_blank')}
                                    title="Navigate with Waze"
                                    className="tw-p-1 tw-rounded-lg hover:tw-bg-surface-3 tw-transition-all"
                                ><WazeIcon /></button>
                                <button
                                    onClick={() => window.open(`https://maps.apple.com/?q=${encodeURIComponent(activity.location)}`, '_blank')}
                                    title="Open in Apple Maps"
                                    className="tw-p-1 tw-rounded-lg hover:tw-bg-surface-3 tw-transition-all"
                                ><AppleMapsIcon /></button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Notes */}
                {activity.notes && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-surface-3/40 tw-border tw-border-surface-border/30">
                        <p className="tw-text-xs tw-font-semibold tw-text-muted tw-uppercase tw-tracking-wide tw-mb-1.5">Notes</p>
                        <p className="tw-text-sm tw-text-muted-fore tw-leading-relaxed" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                            {activity.notes}
                        </p>
                    </div>
                )}

                {/* Flight Details - For "flight" activities (in-app or email-imported) */}
                {isFlightActivity && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-sky-500/10 tw-border tw-border-sky-500/20">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                            <FlightTakeoffIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
                            <span className="tw-text-sm tw-font-bold tw-text-sky-400">Flight Information</span>
                            {scheduleHistory.length > 0 && (
                                <button
                                    type="button"
                                    title="Schedule history"
                                    aria-label="Schedule history"
                                    onClick={() => setShowScheduleHistory(v => !v)}
                                    className={`tw-ml-auto tw-w-6 tw-h-6 tw-rounded-full tw-border tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center tw-transition-all ${showScheduleHistory ? 'tw-bg-amber-400/20 tw-border-amber-400 tw-text-amber-300' : 'tw-border-amber-400 tw-text-amber-400 hover:tw-bg-amber-400/10'}`}
                                >H</button>
                            )}
                        </div>
                        {hasFlightInfo ? (
                            <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                {flightInfoRows.map((row, index) => (
                                    <div key={`${row.label}-${index}`}>
                                        <p className="tw-text-xs tw-text-muted tw-mb-0.5">{row.label}</p>
                                        <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{row.value}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="tw-text-sm tw-text-muted">No flight information available.</p>
                        )}
                        {activity.structured_data?.details?.seatNumber && !flightInfoRows.some(r => r.label === 'Seat Number') && (
                            <div className="tw-mt-2">
                                <p className="tw-text-xs tw-text-muted tw-mb-0.5">Seat Number</p>
                                <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{activity.structured_data.details.seatNumber}</p>
                            </div>
                        )}
                        {showScheduleHistory && scheduleHistory.length > 0 && (
                            <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-sky-500/20">
                                <p className="tw-text-xs tw-font-bold tw-text-amber-400 tw-mb-2">Previous schedules</p>
                                {rollbackError && <p className="tw-text-xs tw-text-red-400 tw-mb-2">{rollbackError}</p>}
                                <div className="tw-flex tw-flex-col tw-gap-2">
                                    {scheduleHistory.map((h, i) => scheduleHistory.length - 1 - i).map((idx) => {
                                        const h = scheduleHistory[idx];
                                        const when = h.start_date_time
                                            || (h.day ? `Day ${h.day}${h.start_time ? ' ' + String(h.start_time).slice(0, 5) : ''}` : 'Unknown time');
                                        return (
                                            <div key={idx} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-2 tw-rounded-lg tw-bg-surface-3/40 tw-border tw-border-surface-border/40">
                                                <div className="tw-min-w-0">
                                                    <p className="tw-text-sm tw-font-semibold tw-text-muted-fore tw-truncate">{when}{h.flight_number ? ` · ${h.flight_number}` : ''}</p>
                                                    <p className="tw-text-[10px] tw-text-muted">superseded{h.source ? ` · ${h.source}` : ''}{h.changed_at ? ` · ${h.changed_at}` : ''}</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled={rollbackBusy === idx}
                                                    onClick={() => handleRollback(idx)}
                                                    className="tw-shrink-0 tw-px-3 tw-py-1 tw-rounded-lg tw-text-xs tw-font-bold tw-border tw-border-amber-400 tw-text-amber-400 hover:tw-bg-amber-400/10 disabled:tw-opacity-50"
                                                >{rollbackBusy === idx ? 'Restoring…' : 'Restore'}</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Transportation Options - For "Travel from" activities */}
                {activity.title?.startsWith('Travel from') && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-surface-3/50 tw-border tw-border-surface-border/40">
                        {activity.subtype === 'flight' && (
                            <div className="tw-mb-3">
                                <p className="tw-text-sm tw-font-bold tw-text-muted-fore tw-mb-2">Flight Information</p>
                                {hasFlightInfo ? (
                                    <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                        {flightInfoRows.map((row, index) => (
                                            <div key={`${row.label}-${index}`}>
                                                <p className="tw-text-xs tw-text-muted tw-mb-0.5">{row.label}</p>
                                                <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{row.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="tw-text-sm tw-text-muted">No flight information was received yet.</p>
                                )}
                            </div>
                        )}
                        {activity.subtype === 'walk' && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <DirectionsWalkIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                <div>
                                    <p className="tw-text-sm tw-font-bold tw-text-muted-fore">Walking Distance</p>
                                    <p className="tw-text-xs tw-text-muted">This is a short distance that can be covered on foot (under 1 hour walk)</p>
                                </div>
                            </div>
                        )}
                        {activity.subtype === 'car' && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <DirectionsCarIcon sx={{ color: '#60a5fa', fontSize: 20 }} />
                                <div>
                                    <p className="tw-text-sm tw-font-bold tw-text-muted-fore">Car or Train Recommended</p>
                                    <p className="tw-text-xs tw-text-muted">This journey is best by car, taxi, bus, or train (under 2 hours driving)</p>
                                </div>
                            </div>
                        )}
                        {activity.subtype === 'flight' && (
                            <>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <FlightTakeoffIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
                                    <span className="tw-text-sm tw-font-bold tw-text-muted-fore">Book Flights</span>
                                    {loadingFlightPrice && (
                                        <CircularProgress size={14} sx={{ ml: 1 }} />
                                    )}
                                </div>
                                {getActivityDate() && (
                                    <p className="tw-text-xs tw-text-sky-400 tw-font-semibold tw-mb-1">Travel Date: {formatDateForDisplay(getActivityDate())}</p>
                                )}
                                {flightPrice && !loadingFlightPrice && (
                                    <p className="tw-text-xl tw-font-bold tw-text-muted-fore tw-mb-1">From ${Math.ceil(flightPrice)}</p>
                                )}
                                <p className="tw-text-xs tw-text-muted tw-mb-3">
                                    {flightPrice ? 'Compare prices across multiple booking sites' : 'Search for flights with your travel date pre-filled'}
                                </p>
                                {/* Expedia Button */}
                                {activity.expedia_affiliate_link && (
                                    <button className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-bg-[#003580] hover:tw-bg-[#00244d] tw-text-white tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-2 tw-transition-all"
                                        onClick={() => window.open(getExpediaLinkWithDates() || activity.expedia_affiliate_link, '_blank')}>
                                        <FlightTakeoffIcon sx={{ fontSize: 16 }} /> Search Flights on Expedia
                                    </button>
                                )}
                                {/* Skyscanner Button */}
                                {activity.skyscanner_affiliate_link && (
                                    <button className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border tw-border-sky-400 tw-text-sky-400 hover:tw-bg-sky-400/10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-2 tw-transition-all"
                                        onClick={() => window.open(activity.skyscanner_affiliate_link, '_blank')}>
                                        <FlightTakeoffIcon sx={{ fontSize: 16 }} /> Compare on Skyscanner
                                    </button>
                                )}
                                {/* Update Flight Info Button */}
                                <button className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border tw-border-blue-400 tw-text-blue-400 hover:tw-bg-blue-400/10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-1 tw-transition-all"
                                    onClick={() => {
                                        handleClose();
                                        const event = new CustomEvent('openTransportationModal', { 
                                            detail: { 
                                                dayLabel: getActivityDate() || null,
                                                destination: activity.destination || activity.location || '',
                                                date: getActivityDate()
                                            } 
                                        });
                                        window.dispatchEvent(event);
                                    }}>
                                    <FlightTakeoffIcon sx={{ fontSize: 16 }} /> Update Flight Information
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Price / cost — shown when captured from import or email parsing */}
                {costSummary && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-emerald-500/10 tw-border tw-border-emerald-500/25">
                        <p className="tw-text-xs tw-text-muted tw-mb-0.5">
                            {(activity.type === 'lodging' || activity.type === 'Lodging') ? 'Lodging cost' : 'Price'}
                        </p>
                        <p className="tw-text-base tw-font-bold tw-text-emerald-400">{costSummary}</p>
                    </div>
                )}

                {/* Booking Information - Show if booked */}
                {isBooked && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-emerald-500/10 tw-border tw-border-emerald-500/25">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                            <CheckCircleIcon sx={{ color: '#34d399', fontSize: 20 }} />
                            <span className="tw-text-sm tw-font-bold tw-text-emerald-400">Booking Confirmed</span>
                        </div>
                        {activity.reference_ticket_no && (
                            <div className="tw-mb-1.5">
                                <p className="tw-text-xs tw-text-muted">Reference / Ticket #</p>
                                <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{activity.reference_ticket_no}</p>
                            </div>
                        )}
                        {activity.booking_agency && (
                            <div>
                                <p className="tw-text-xs tw-text-muted">Booking Information</p>
                                <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{activity.booking_agency}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Live status placeholder (more than 48h before departure) ── */}
                {isLiveFlight && !isWithinFlightStatusWindow() && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-surface-3/40 tw-border tw-border-white/10">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <FlightTakeoffIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <span className="tw-text-sm tw-font-semibold tw-text-muted-fore">Live Flight Status</span>
                        </div>
                        <p className="tw-text-xs tw-text-muted tw-mt-1.5">
                            Live data available 48 hours prior to departure.
                        </p>
                    </div>
                )}

                {/* ── Live Flight Status (only within 48h of departure) ── */}
                {isLiveFlight && isWithinFlightStatusWindow() && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-sky-500/10 tw-border tw-border-sky-500/25">
                        {/* Header */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <FlightTakeoffIcon sx={{ color: '#38bdf8', fontSize: 18 }} />
                                <span className="tw-text-sm tw-font-bold tw-text-sky-400">Live Flight Status</span>
                                {flightStatus && flightStatus.source && (
                                    <span className="tw-text-xs tw-text-muted tw-font-normal">via {flightStatus.source}</span>
                                )}
                            </div>
                            {!loadingFlightStatus && (
                                <button
                                    onClick={() => runFlightStatusFetch(true)}
                                    title="Force update"
                                    className="tw-p-1 tw-rounded-lg tw-text-muted hover:tw-text-sky-400 hover:tw-bg-sky-500/10 tw-transition-all"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                                </button>
                            )}
                        </div>

                        {/* Loading */}
                        {loadingFlightStatus && (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-py-2">
                                <CircularProgress size={16} sx={{ color: '#38bdf8' }} />
                                <span className="tw-text-xs tw-text-muted">Fetching live status…</span>
                            </div>
                        )}

                        {/* Route mismatch: the looked-up flight number does not fly the
                            itinerary's origin→destination. Warn instead of showing the
                            wrong flight's gate/times (e.g. "OS15" resolving to VIE→BKK). */}
                        {!loadingFlightStatus && flightStatus && flightStatus.route_verified === false && (
                            <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2.5 tw-rounded-lg tw-border-l-4 tw-border-amber-400 tw-bg-amber-400/10">
                                <span className="tw-text-amber-400 tw-mt-0.5">⚠</span>
                                <div className="tw-text-xs tw-text-muted-fore">
                                    <p className="tw-font-semibold">Couldn't verify this flight</p>
                                    <p className="tw-mt-0.5 tw-text-muted">
                                        The airline lists {flightStatus.flight_number || 'this number'} as{' '}
                                        <strong>{flightStatus.matched_route}</strong>, not{' '}
                                        <strong>{flightStatus.expected_route}</strong>. Please verify the flight number.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Status data */}
                        {!loadingFlightStatus && flightStatus && flightStatus.route_verified !== false && (() => {
                            const statusStyle = statusColor(flightStatus.status);
                            const dep = flightStatus.departure || {};
                            const arr = flightStatus.arrival   || {};
                            const hasGateInfo    = dep.gate || arr.gate || arr.baggage_belt || dep.terminal || arr.terminal;
                            // Times are shown from the RESERVATION (its local airport time),
                            // never from the provider — providers report times in mismatched
                            // zones (the scraper emits UTC), so an absolute provider time can be
                            // hours off the booking. We only overlay a *delay* (a within-source
                            // delta) onto the reservation's own local time.
                            const resSd = (activity.structured_data && typeof activity.structured_data === 'object') ? activity.structured_data : {};
                            const resDep = (typeof resSd.start_date_time === 'string' && resSd.start_date_time.length >= 16) ? resSd.start_date_time.slice(11, 16) : null;
                            const resArr = (typeof resSd.end_date_time === 'string' && resSd.end_date_time.length >= 16) ? resSd.end_date_time.slice(11, 16) : null;
                            const DELAY_MIN = 15;
                            const addMinutes = (hhmm, d) => {
                                if (!hhmm) return null;
                                const [h, m] = hhmm.split(':').map(Number);
                                const t = (((h * 60 + m + d) % 1440) + 1440) % 1440;
                                return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
                            };
                            const depDelayMin = (dep.delay_minutes != null && Math.abs(dep.delay_minutes) >= DELAY_MIN) ? dep.delay_minutes : 0;
                            const arrDelayMin = (arr.delay_minutes != null && Math.abs(arr.delay_minutes) >= DELAY_MIN) ? arr.delay_minutes : 0;
                            const depNewTime = depDelayMin ? addMinutes(resDep, depDelayMin) : null;
                            const arrNewTime = arrDelayMin ? addMinutes(resArr, arrDelayMin) : null;
                            const hasTimeInfo    = !!(resDep || resArr);
                            const depDelayed     = depDelayMin !== 0;
                            const arrDelayed     = arrDelayMin !== 0;
                            return (
                                <>
                                    {/* Status badge */}
                                    <div className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-bold tw-mb-3 ${statusStyle.bg} ${statusStyle.text}`}>
                                        <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-current tw-animate-pulse" />
                                        {flightStatus.status || 'Scheduled'}
                                    </div>

                                    {/* Dep / Arr info */}
                                    <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mb-2">
                                        {/* Departure */}
                                        <div className="tw-p-2 tw-rounded-lg tw-bg-surface-3/40">
                                            <p className="tw-text-xs tw-font-bold tw-text-muted tw-uppercase tw-tracking-wide tw-mb-1.5">Departure · {dep.iata || ''}</p>
                                            {dep.terminal && (
                                                <div className="tw-mb-1">
                                                    <p className="tw-text-xs tw-text-muted">Terminal</p>
                                                    <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{dep.terminal}</p>
                                                </div>
                                            )}
                                            {dep.gate && (
                                                <div className="tw-mb-1">
                                                    <p className="tw-text-xs tw-text-muted">Gate</p>
                                                    <p className="tw-text-sm tw-font-bold tw-text-sky-400">{dep.gate}</p>
                                                </div>
                                            )}
                                            {resDep && (
                                                <div>
                                                    <p className="tw-text-xs tw-text-muted">Time</p>
                                                    <p className={`tw-text-sm tw-font-semibold ${depDelayed ? 'tw-text-amber-400' : 'tw-text-muted-fore'}`}>
                                                        {depNewTime || resDep}
                                                        {depNewTime && (
                                                            <span className="tw-text-xs tw-text-muted tw-line-through tw-ml-1">{resDep}</span>
                                                        )}
                                                    </p>
                                                    {depDelayed && (
                                                        <p className="tw-text-xs tw-text-amber-400">{depDelayMin > 0 ? '+' : ''}{depDelayMin} min delay</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Arrival */}
                                        <div className="tw-p-2 tw-rounded-lg tw-bg-surface-3/40">
                                            <p className="tw-text-xs tw-font-bold tw-text-muted tw-uppercase tw-tracking-wide tw-mb-1.5">Arrival · {arr.iata || ''}</p>
                                            {arr.terminal && (
                                                <div className="tw-mb-1">
                                                    <p className="tw-text-xs tw-text-muted">Terminal</p>
                                                    <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{arr.terminal}</p>
                                                </div>
                                            )}
                                            {arr.gate && (
                                                <div className="tw-mb-1">
                                                    <p className="tw-text-xs tw-text-muted">Gate</p>
                                                    <p className="tw-text-sm tw-font-bold tw-text-sky-400">{arr.gate}</p>
                                                </div>
                                            )}
                                            {arr.baggage_belt && (
                                                <div className="tw-mb-1">
                                                    <p className="tw-text-xs tw-text-muted">Baggage Belt</p>
                                                    <p className="tw-text-sm tw-font-bold tw-text-purple-400">{arr.baggage_belt}</p>
                                                </div>
                                            )}
                                            {resArr && (
                                                <div>
                                                    <p className="tw-text-xs tw-text-muted">Time</p>
                                                    <p className={`tw-text-sm tw-font-semibold ${arrDelayed ? 'tw-text-amber-400' : 'tw-text-muted-fore'}`}>
                                                        {arrNewTime || resArr}
                                                        {arrNewTime && (
                                                            <span className="tw-text-xs tw-text-muted tw-line-through tw-ml-1">{resArr}</span>
                                                        )}
                                                    </p>
                                                    {arrDelayed && (
                                                        <p className="tw-text-xs tw-text-amber-400">{arrDelayMin > 0 ? '+' : ''}{arrDelayMin} min delay</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Info message when no live gate/time data yet */}
                                    {!hasGateInfo && !hasTimeInfo && flightStatus.info && (
                                        <p className="tw-text-xs tw-text-muted tw-mt-1">{flightStatus.info}</p>
                                    )}

                                    {/* Aircraft / duration / operating carrier */}
                                    {flightStatus.aircraft && (
                                        <p className="tw-text-xs tw-text-muted tw-mt-1">Aircraft: {flightStatus.aircraft}</p>
                                    )}
                                    {flightStatus.duration && (
                                        <p className="tw-text-xs tw-text-muted tw-mt-1">Flight time: {flightStatus.duration}</p>
                                    )}
                                    {flightStatus.operated_by && (
                                        <p className="tw-text-xs tw-text-muted tw-mt-1">Operated by: {flightStatus.operated_by}</p>
                                    )}

                                    {/* FlightAware deep-link */}
                                    <button
                                        onClick={() => {
                                            // The backend permalink pin-points the departure with a
                                            // /history/{date}/{time}Z/{orig}/{dest} suffix — but that form
                                            // only resolves for flights that have ALREADY flown; for a
                                            // scheduled/future flight it 404s. Reduce it to the flight-ident
                                            // page (…/live/flight/{ident}), which reliably resolves (200) and
                                            // shows the upcoming instance. Keeps the correct ident (e.g. AUA15).
                                            let url = flightStatus.flightaware_url;
                                            const identMatch = url && url.match(/\/live\/flight\/([^/?#]+)/);
                                            if (identMatch) {
                                                url = `https://www.flightaware.com/live/flight/${identMatch[1]}`;
                                            } else {
                                                const faId = buildFlightAwareId(rawFlightNum);
                                                url = `https://www.flightaware.com/live/flight/${faId}`;
                                            }
                                            window.open(url, '_blank');
                                        }}
                                        className="tw-mt-2 tw-text-xs tw-text-sky-400 hover:tw-underline tw-flex tw-items-center tw-gap-1"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                        View on FlightAware
                                    </button>
                                </>
                            );
                        })()}

                        {/* Last-pulled time + force update */}
                        {!loadingFlightStatus && flightStatus && (
                            <div className="tw-mt-3 tw-pt-2 tw-border-t tw-border-white/10 tw-flex tw-items-center tw-justify-between tw-gap-2">
                                {flightStatus.fetched_at && (
                                    <span className="tw-text-xs tw-text-muted">Updated {formatFetchedAgo(flightStatus.fetched_at)}</span>
                                )}
                                <button
                                    onClick={() => runFlightStatusFetch(true)}
                                    className="tw-text-xs tw-font-semibold tw-text-sky-400 hover:tw-text-sky-300 tw-flex tw-items-center tw-gap-1 tw-ml-auto"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                                    Force update
                                </button>
                            </div>
                        )}

                        {/* Not yet fetched hint */}
                        {!loadingFlightStatus && !flightStatus && (
                            <p className="tw-text-xs tw-text-muted">Status unavailable — tap refresh to try again.</p>
                        )}
                    </div>
                )}

                {/* AI Agent Working - Show while loading Viator details */}
                {!isBooked && activity.type !== 'flight' && loadingViator && (
                    <div className="tw-mb-4 tw-p-3 tw-rounded-xl tw-bg-blue-500/10 tw-border tw-border-blue-500/25 tw-flex tw-items-center tw-gap-3">
                        <AIIcon sx={{ color: '#60a5fa', fontSize: 28, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <div>
                            <p className="tw-text-sm tw-font-bold tw-text-blue-400 tw-mb-0.5">AI Agent in action...</p>
                            <p className="tw-text-xs tw-text-muted">Checking the offers and analyzing match quality</p>
                        </div>
                    </div>
                )}

                {/* AI Comparison - Show for all match qualities */}
                {!isBooked && activity.type !== 'flight' && !loadingViator && viatorDetails && viatorDetails.ai_comparison && (
                    <div className={`tw-mb-3 tw-p-3 tw-rounded-xl tw-border ${
                        viatorDetails.ai_comparison.match_quality === 'poor' || viatorDetails.ai_comparison.match_quality === 'ai_no_match'
                            ? 'tw-bg-amber-500/10 tw-border-amber-500/25'
                            : 'tw-bg-blue-500/10 tw-border-blue-500/25'
                    }`}>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                            <AIIcon sx={{
                                color: viatorDetails.ai_comparison.match_quality === 'poor' || viatorDetails.ai_comparison.match_quality === 'ai_no_match' ? '#f59e0b' : '#60a5fa',
                                fontSize: 22
                            }} />
                            <span className={`tw-text-sm tw-font-bold ${
                                viatorDetails.ai_comparison.match_quality === 'poor' || viatorDetails.ai_comparison.match_quality === 'ai_no_match'
                                    ? 'tw-text-amber-400' : 'tw-text-blue-400'
                            }`}>{t('ai_agent_analysis')}</span>
                            {typeof viatorDetails.ai_comparison === 'object' &&
                             viatorDetails.ai_comparison.match_quality &&
                             typeof viatorDetails.ai_comparison.match_quality === 'string' && (
                                <span className={`tw-text-xs tw-font-bold tw-text-white tw-px-2 tw-py-0.5 tw-rounded-full ${
                                    viatorDetails.ai_comparison.match_quality === 'excellent' || viatorDetails.ai_comparison.match_quality === 'exact' ? 'tw-bg-emerald-500' :
                                    viatorDetails.ai_comparison.match_quality === 'good' ? 'tw-bg-blue-500' :
                                    viatorDetails.ai_comparison.match_quality === 'fair' ? 'tw-bg-amber-500' :
                                    viatorDetails.ai_comparison.match_quality === 'ai_no_match' ? 'tw-bg-gray-500' : 'tw-bg-red-500'
                                }`}>
                                    {t(`match_quality_${viatorDetails.ai_comparison.match_quality}`)}
                                </span>
                            )}
                        </div>
                        <p className="tw-text-sm tw-text-muted-fore tw-leading-relaxed">
                            {typeof viatorDetails.ai_comparison === 'object' && viatorDetails.ai_comparison.summary
                                ? String(viatorDetails.ai_comparison.summary)
                                : (typeof viatorDetails.ai_comparison === 'string' ? viatorDetails.ai_comparison : '')}
                        </p>
                    </div>
                )}

                {/* Loading state while fetching comparison - AI validating */}
                {!isBooked && activity.type !== 'flight' && loadingComparison && (
                    <div className="tw-mb-3 tw-p-4 tw-rounded-xl tw-bg-emerald-500/10 tw-border tw-border-emerald-500/25 tw-flex tw-items-center tw-gap-3">
                        <div className="tw-relative tw-flex tw-items-center tw-justify-center">
                            <AIIcon sx={{ fontSize: 28, color: '#34d399' }} />
                            <div className="tw-absolute tw-w-12 tw-h-12 tw-rounded-full tw-border-2 tw-border-emerald-400 tw-animate-ping tw-opacity-50" />
                        </div>
                        <div>
                            <p className="tw-text-sm tw-font-bold tw-text-emerald-400">{t('ai_validating_offers')}</p>
                            <p className="tw-text-xs tw-text-muted">{t('comparing_viator_gyg')}</p>
                        </div>
                    </div>
                )}

                {/* Viator Full Details - Show when we have viator details (regardless of match quality) */}
                {!isBooked && activity.type !== 'flight' && viatorDetails && viatorDetails.success && (
                    <div className="tw-mb-3 tw-p-4 tw-rounded-xl tw-bg-emerald-500/10 tw-border tw-border-emerald-500/25">
                        {/* Header */}
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                            <ShoppingBagIcon sx={{ color: '#34d399', fontSize: 22 }} />
                            <span className="tw-text-base tw-font-bold tw-text-emerald-400">Book on Viator</span>
                            {viatorDetails.ai_comparison?.match_quality === 'poor' && (
                                <span className="tw-text-xs tw-font-bold tw-text-white tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-amber-500 tw-ml-1">
                                    {t('different_activity') || 'Different activity'}
                                </span>
                            )}
                        </div>

                        {loadingViator && (
                            <div className="tw-flex tw-justify-center tw-py-6">
                                <CircularProgress size={36} sx={{ color: '#34d399' }} />
                            </div>
                        )}

                        {!loadingViator && viatorDetails && (
                            <>
                                {/* Price and Book Button */}
                                <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3 tw-mb-3">
                                    <div>
                                        {viatorDetails.viator_price && (
                                            <div className="tw-flex tw-items-baseline tw-gap-1">
                                                <span className="tw-text-2xl tw-font-bold tw-text-emerald-400">{formatViatorQuickPrice(viatorDetails.viator_price)}</span>
                                                <span className="tw-text-xs tw-text-muted">per person</span>
                                            </div>
                                        )}
                                        {viatorDetails.reviews && viatorDetails.reviews.rating && (
                                            <p className="tw-text-xs tw-text-muted">⭐ {viatorDetails.reviews.rating.toFixed(1)} ({viatorDetails.reviews.count} reviews)</p>
                                        )}
                                    </div>
                                    {viatorDetails.viator_link && (
                                        <button
                                            className="tw-px-5 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-bg-emerald-600 hover:tw-bg-emerald-500 tw-text-white tw-flex tw-items-center tw-gap-2 tw-transition-all"
                                            onClick={() => window.open(viatorDetails.viator_link, '_blank')}
                                        >
                                            <ShoppingBagIcon sx={{ fontSize: 16 }} /> Book Now
                                        </button>
                                    )}
                                </div>

                                {/* Product Title */}
                                {viatorDetails.title && viatorDetails.title !== activity.title && (
                                    <div className="tw-mb-3">
                                        <p className="tw-text-xs tw-text-muted tw-mb-0.5">Viator Product</p>
                                        <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{viatorDetails.title}</p>
                                    </div>
                                )}

                                {/* Tags */}
                                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-3">
                                    {viatorDetails.itinerary_type && viatorDetails.itinerary_type !== '0' && (
                                        <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-bg-blue-500/15 tw-text-blue-300 tw-capitalize">{viatorDetails.itinerary_type.replace(/_/g, ' ')}</span>
                                    )}
                                    {viatorDetails.duration && viatorDetails.duration.fixedDurationInMinutes && (
                                        <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-bg-amber-500/15 tw-text-amber-300">
                                            ⏱ {Math.floor(viatorDetails.duration.fixedDurationInMinutes / 60)}h {viatorDetails.duration.fixedDurationInMinutes % 60}m
                                        </span>
                                    )}
                                    {viatorDetails.duration && viatorDetails.duration.variableDurationFromMinutes && (
                                        <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-bg-amber-500/15 tw-text-amber-300">
                                            ⏱ {Math.floor(viatorDetails.duration.variableDurationFromMinutes / 60)}-{Math.floor(viatorDetails.duration.variableDurationToMinutes / 60)} hours
                                        </span>
                                    )}
                                    {viatorDetails.private_tour && (
                                        <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-bg-purple-500/15 tw-text-purple-300">Private Tour</span>
                                    )}
                                    {viatorDetails.skip_the_line && (
                                        <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-bg-emerald-500/15 tw-text-emerald-300">Skip the Line</span>
                                    )}
                                    {viatorDetails.max_travelers_shared_tour && (
                                        <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-bg-rose-500/15 tw-text-rose-300">Max {viatorDetails.max_travelers_shared_tour} travelers</span>
                                    )}
                                </div>

                                {/* Description */}
                                {viatorDetails.description && (
                                    <details className="tw-mb-2 tw-rounded-xl tw-overflow-hidden" open>
                                        <summary className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-surface-3/50 tw-text-sm tw-font-bold tw-text-muted-fore tw-cursor-pointer tw-select-none hover:tw-bg-surface-3/70 tw-transition-colors">
                                            Overview
                                            <span className="tw-text-muted tw-text-xs">▼</span>
                                        </summary>
                                        <div className="tw-p-3 tw-pt-2 tw-bg-surface-3/30">
                                            <p className="tw-text-sm tw-text-muted tw-leading-relaxed tw-whitespace-pre-line tw-break-words">{viatorDetails.description}</p>
                                        </div>
                                    </details>
                                )}

                                {/* Itinerary */}
                                {viatorDetails.itinerary && viatorDetails.itinerary.length > 0 && (
                                    <details className="tw-mb-2 tw-rounded-xl tw-overflow-hidden">
                                        <summary className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-surface-3/50 tw-text-sm tw-font-bold tw-text-muted-fore tw-cursor-pointer tw-select-none hover:tw-bg-surface-3/70 tw-transition-colors">
                                            Itinerary
                                            <span className="tw-text-muted tw-text-xs">▼</span>
                                        </summary>
                                        <div className="tw-p-3 tw-pt-2 tw-bg-surface-3/30 tw-space-y-3">
                                            {viatorDetails.itinerary.map((item, idx) => (
                                                <div key={idx}>
                                                    <p className="tw-text-sm tw-font-semibold tw-text-muted-fore tw-mb-0.5 tw-break-words">{item.title || `Stop ${idx + 1}`}</p>
                                                    <p className="tw-text-xs tw-text-muted tw-break-words">{item.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}

                                {/* Additional Info */}
                                {viatorDetails.additional_info && viatorDetails.additional_info.length > 0 && (
                                    <details className="tw-mb-2 tw-rounded-xl tw-overflow-hidden">
                                        <summary className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-surface-3/50 tw-text-sm tw-font-bold tw-text-muted-fore tw-cursor-pointer tw-select-none hover:tw-bg-surface-3/70 tw-transition-colors">
                                            Additional Information
                                            <span className="tw-text-muted tw-text-xs">▼</span>
                                        </summary>
                                        <div className="tw-p-3 tw-pt-2 tw-bg-surface-3/30">
                                            <ul className="tw-list-disc tw-pl-4 tw-space-y-1">
                                                {viatorDetails.additional_info.map((item, idx) => (
                                                    <li key={idx} className="tw-text-xs tw-text-muted tw-break-words">
                                                        {typeof item === 'string' ? item : item.description || item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </details>
                                )}

                                {/* Product Options / Variants */}
                                {viatorDetails.product_options && viatorDetails.product_options.length > 0 && (
                                    <details className="tw-mb-2 tw-rounded-xl tw-overflow-hidden">
                                        <summary className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-surface-3/50 tw-text-sm tw-font-bold tw-text-muted-fore tw-cursor-pointer tw-select-none hover:tw-bg-surface-3/70 tw-transition-colors">
                                            Available Options ({viatorDetails.product_options.length})
                                            <span className="tw-text-muted tw-text-xs">▼</span>
                                        </summary>
                                        <div className="tw-p-3 tw-pt-2 tw-bg-surface-3/30 tw-space-y-3">
                                            {viatorDetails.product_options.map((option, idx) => (
                                                <div key={idx} className={idx < viatorDetails.product_options.length - 1 ? 'tw-pb-3 tw-border-b tw-border-surface-border/40' : ''}>
                                                    <p className="tw-text-sm tw-font-semibold tw-text-muted-fore tw-mb-0.5">{option.title}</p>
                                                    {option.description && <p className="tw-text-xs tw-text-muted tw-mb-1 tw-break-words">{option.description}</p>}
                                                    {option.productOptionCode && (
                                                        <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-border tw-border-surface-border/60 tw-text-muted">Code: {option.productOptionCode}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}

                                {/* Pricing Information */}
                                {viatorDetails.pricing_info && viatorDetails.pricing_info.ageBands && (
                                    <details className="tw-mb-2 tw-rounded-xl tw-overflow-hidden">
                                        <summary className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-surface-3/50 tw-text-sm tw-font-bold tw-text-muted-fore tw-cursor-pointer tw-select-none hover:tw-bg-surface-3/70 tw-transition-colors">
                                            Pricing Details
                                            <span className="tw-text-muted tw-text-xs">▼</span>
                                        </summary>
                                        <div className="tw-p-3 tw-pt-2 tw-bg-surface-3/30 tw-space-y-1">
                                            <p className="tw-text-xs tw-text-muted tw-mb-2">
                                                <strong className="tw-text-muted-fore">Pricing Type:</strong> {viatorDetails.pricing_info.type?.replace(/_/g, ' ') || 'Per Person'}
                                            </p>
                                            {viatorDetails.pricing_info.ageBands.map((band, idx) => (
                                                <p key={idx} className="tw-text-xs tw-text-muted">
                                                    <strong className="tw-text-muted-fore">{band.ageBand}:</strong> Ages {band.startAge}-{band.endAge}
                                                    {band.minTravelersPerBooking > 0 && ` (Min: ${band.minTravelersPerBooking})`}
                                                    {band.maxTravelersPerBooking && ` (Max: ${band.maxTravelersPerBooking})`}
                                                </p>
                                            ))}
                                        </div>
                                    </details>
                                )}

                                {/* Language Guides */}
                                {viatorDetails.language_guides && viatorDetails.language_guides.length > 0 && (
                                    <div className="tw-mt-3">
                                        <p className="tw-text-xs tw-font-bold tw-text-muted-fore tw-mb-1.5">Available Languages</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1">
                                            {viatorDetails.language_guides.map((lang, idx) => {
                                                const label = lang.language || lang;
                                                if (!label || label === '0' || label === 0) return null;
                                                return (
                                                    <span key={idx} className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-lg tw-border tw-border-surface-border/60 tw-text-muted">{label}</span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {loadingViator && (
                            <div className="tw-flex tw-justify-center tw-py-4">
                                <CircularProgress size={36} sx={{ color: '#34d399' }} />
                            </div>
                        )}

                        {/* REMOVED: Redundant AI Recommendation box - using AI Agent Analysis at top instead */}

                        {/* Fallback: show old Viator button if no comparison */}
                        {!loadingViator && viatorDetails && !comparison && (
                            <div className="tw-mt-3">
                                <button
                                    className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-bg-emerald-600 hover:tw-bg-emerald-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all"
                                    onClick={() => {
                                        if (viatorDetails.viator_link) {
                                            window.open(viatorDetails.viator_link, '_blank');
                                        }
                                    }}
                                >
                                    <ShoppingBagIcon sx={{ fontSize: 16 }} /> Book on Viator
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* GetYourGuide Card - Show when we have Viator details */}
                {!isBooked && activity.type !== 'flight' && viatorDetails?.success && (
                    <div className="tw-mb-3 tw-p-4 tw-rounded-xl tw-bg-orange-500/10 tw-border tw-border-orange-500/25">
                        <p className="tw-text-sm tw-font-bold tw-text-muted-fore tw-mb-2">Alternative Booking Option</p>
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <p className="tw-text-base tw-font-bold tw-text-orange-400">GetYourGuide</p>
                            <p className="tw-text-sm tw-text-muted-fore">{getActivityDisplayTitle(activity)}</p>
                            <p className="tw-text-xs tw-text-muted tw-italic">{t('search_alternatives_gyg')}</p>
                            <button
                                className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border tw-border-orange-500/50 tw-text-orange-400 hover:tw-bg-orange-500/10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all"
                                onClick={() => {
                                    if (activity.gyg_link) {
                                        window.open(activity.gyg_link, '_blank');
                                    } else {
                                        window.open(`https://www.getyourguide.com/s/?q=${bookingSearchQuery}&partner_id=CRCUT20`, '_blank');
                                    }
                                }}
                            >
                                <ShoppingBagIcon sx={{ fontSize: 16 }} /> {t('search_on_gyg')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Booking Options: GetYourGuide - Show when we have GYG ID but no Viator details */}
                {!isBooked && activity.type !== 'flight' && !activity.title?.startsWith('Travel from') && activity.gyg_tour_id && !viatorDetails?.success && (
                    <div className="tw-mb-3 tw-p-4 tw-rounded-xl tw-bg-orange-500/10 tw-border tw-border-orange-500/25">
                        <p className="tw-text-sm tw-font-bold tw-text-muted-fore tw-mb-3">Booking Options</p>
                        {activity.gyg_tour_id && (
                            <div className="tw-flex tw-flex-col tw-gap-2">
                                <p className="tw-text-base tw-font-bold tw-text-orange-400">GetYourGuide</p>
                                <p className="tw-text-sm tw-text-muted-fore">{getActivityDisplayTitle(activity)}</p>
                                <p className="tw-text-xs tw-text-muted tw-italic">{t('search_alternatives_gyg')}</p>
                                <button
                                    className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border tw-border-orange-500/50 tw-text-orange-400 hover:tw-bg-orange-500/10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all"
                                    onClick={() => {
                                        if (activity.gyg_link) {
                                            window.open(activity.gyg_link, '_blank');
                                        } else {
                                            window.open(`https://www.getyourguide.com/s/?q=${activityTitleSearchQuery}&partner_id=CRCUT20`, '_blank');
                                        }
                                    }}
                                >
                                    <ShoppingBagIcon sx={{ fontSize: 16 }} /> {t('search_on_gyg')}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Direct Viator link - When activity has viator_link in DB but no full details loaded */}
                {!isBooked && activity.type !== 'flight' && !activity.title?.startsWith('Travel from') &&
                 !loadingViator && !viatorDetails?.success && activity.viator_link && (
                    <div className="tw-mb-3 tw-p-4 tw-rounded-xl tw-bg-emerald-500/10 tw-border tw-border-emerald-500/25">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                            <ShoppingBagIcon sx={{ color: '#34d399', fontSize: 22 }} />
                            <span className="tw-text-base tw-font-bold tw-text-emerald-400">Book on Viator</span>
                        </div>
                        {activity.viator_price && (
                            <div className="tw-flex tw-items-baseline tw-gap-1 tw-mb-3">
                                <span className="tw-text-2xl tw-font-bold tw-text-emerald-400">{formatViatorQuickPrice(activity.viator_price)}</span>
                                <span className="tw-text-xs tw-text-muted">per person</span>
                            </div>
                        )}
                        <button
                            className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-bg-emerald-600 hover:tw-bg-emerald-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all"
                            onClick={() => window.open(activity.viator_link, '_blank')}
                        >
                            <ShoppingBagIcon sx={{ fontSize: 16 }} /> Book on Viator
                        </button>
                        {activity.gyg_link && (
                            <button
                                className="tw-w-full tw-mt-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border tw-border-orange-500/50 tw-text-orange-400 hover:tw-bg-orange-500/10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all"
                                onClick={() => window.open(activity.gyg_link, '_blank')}
                            >
                                <ShoppingBagIcon sx={{ fontSize: 16 }} /> Also on GetYourGuide
                            </button>
                        )}
                    </div>
                )}

                {/* Fallback: No Viator API data, no gyg_tour_id, no stored viator_link — GYG search only with affiliate */}
                {!isBooked && activity.type !== 'flight' && !activity.title?.startsWith('Travel from') &&
                 !loadingViator && !viatorDetails?.success && !activity.gyg_tour_id && !activity.viator_link && (
                    <div className="tw-mb-3 tw-p-4 tw-rounded-xl tw-bg-orange-500/10 tw-border tw-border-orange-500/25">
                        <p className="tw-text-sm tw-font-bold tw-text-muted-fore tw-mb-1">Book This Activity</p>
                        <p className="tw-text-xs tw-text-muted tw-mb-3">Search for this activity on GetYourGuide</p>
                        <button
                            className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border tw-border-orange-500/50 tw-text-orange-400 hover:tw-bg-orange-500/10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all"
                            onClick={() => window.open(`https://www.getyourguide.com/s/?q=${bookingSearchQuery}&partner_id=CRCUT20`, '_blank')}
                        >
                            <ShoppingBagIcon sx={{ fontSize: 16 }} /> {t('search_on_gyg')}
                        </button>
                    </div>
                )}

                <div className="tw-border-t tw-border-surface-border/40 tw-my-3" />

                {/* Image Gallery */}
                {activity.title && activity.type !== 'flight' && activity.type !== 'transportation' && activity.type !== 'lodging' && activity.type !== 'Lodging' && (
                    <ActivityImageGallery 
                        location={activity.title}
                        onImageClick={(image) => {
                            // Optional: track image clicks or perform additional actions
                        }}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
                <button
                    onClick={handleClose}
                    className="tw-px-5 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent hover:tw-bg-accent/90 tw-text-white tw-transition-all tw-duration-150"
                >
                    Close
                </button>
            </DialogActions>
        </Dialog>

        {/* Source email viewer */}

        {sourceEmailMsg && (
            <Dialog
                open={Boolean(sourceEmailMsg)}
                onClose={() => setSourceEmailMsg(null)}
                fullScreen
                sx={{ zIndex: 1500 }}
                PaperProps={{ style: { backgroundColor: '#1a1d2e', color: '#f0f0f5' } }}
            >
                <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-white/10" style={{ backgroundColor: '#242740' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6C63FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="tw-font-bold tw-text-base tw-truncate" style={{ color: '#f0f0f5' }}>{sourceEmailMsg.subject || '(no subject)'}</div>
                        <div className="tw-text-xs" style={{ color: 'rgba(240,240,245,0.55)' }}>From: {sourceEmailMsg.sender_email}</div>
                    </div>
                    <button
                        onClick={() => setSourceEmailMsg(null)}
                        className="tw-p-1.5 tw-rounded-xl tw-transition-all"
                        style={{ color: 'rgba(240,240,245,0.55)' }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '24px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.7, color: '#f0f0f5', backgroundColor: '#1a1d2e' }}>
                    {sourceEmailMsg.body || ''}
                </div>
                <div className="tw-flex tw-justify-end tw-px-5 tw-py-3 tw-border-t tw-border-white/10" style={{ backgroundColor: '#242740' }}>
                    <button
                        onClick={() => setSourceEmailMsg(null)}
                        className="tw-px-5 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-text-white tw-transition-all"
                        style={{ backgroundColor: '#6C63FF' }}
                    >
                        Close
                    </button>
                </div>
            </Dialog>
        )}
        </>
    );
};

export default ActivityDetailsModal;
